/**
 * File: adrSuggestionsCache.ts
 * Description: localStorage-backed cache for ADR suggestions state that
 *              survives full-page reloads and browser restarts (used in
 *              StageNavigationBar).
 * Author: GitHub Copilot
 * Created: 2026-05-05
 * Last Modified: 2026-05-05
 */

import { ADRSuggestion } from '../types/adr';

interface ADRSuggestionFeedbackData {
  inputs: {
    projectId: number;
    projectContext: string;
    maxSuggestions: number;
    timestamp: string;
    userInitiated: boolean;
  } | null;
  outputs: {
    suggestions: ADRSuggestion[];
    rawResponse?: string;
    processingTime?: number;
    timestamp: string;
    error?: string;
  } | null;
}

export interface CachedADRSuggestionsState {
  suggestions: ADRSuggestion[];
  hasAutoGenerated: boolean;
  adrSuggestionData: ADRSuggestionFeedbackData;
  suggestionProjectContext: string;
  adrExecutionPlan: Array<Record<string, unknown>> | null;
}

const DEFAULT_CACHED_STATE: CachedADRSuggestionsState = {
  suggestions: [],
  hasAutoGenerated: false,
  adrSuggestionData: { inputs: null, outputs: null },
  suggestionProjectContext: '',
  adrExecutionPlan: null,
};

const STORAGE_KEY_PREFIX = 'adr_suggestions_cache_';

/**
 * Build the localStorage key for a given project.
 *
 * @param projectId - The project identifier.
 */
const buildKey = (projectId: string): string =>
  `${STORAGE_KEY_PREFIX}${projectId}`;

/**
 * Retrieve cached suggestions state for a given project.
 * Reads from localStorage so the data survives reloads and browser restarts.
 *
 * @param projectId - The project identifier (string form of the numeric id).
 * @returns The cached state, or null if no entry exists yet.
 */
export const getADRSuggestionsCache = (
  projectId: string,
): CachedADRSuggestionsState | null => {
  try {
    const raw = localStorage.getItem(buildKey(projectId));
    if (!raw) {return null;}
    return JSON.parse(raw) as CachedADRSuggestionsState;
  } catch {
    return null;
  }
};

/**
 * Merge partial state updates into the cache for a given project.
 * Writes to localStorage so the data survives reloads and browser restarts.
 *
 * @param projectId - The project identifier.
 * @param updates   - Partial state to merge into the existing cached entry.
 */
export const setADRSuggestionsCache = (
  projectId: string,
  updates: Partial<CachedADRSuggestionsState>,
): void => {
  try {
    const current = getADRSuggestionsCache(projectId) ?? DEFAULT_CACHED_STATE;
    const next: CachedADRSuggestionsState = { ...current, ...updates };
    localStorage.setItem(buildKey(projectId), JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (private browsing quota, etc.)
    // Silently degrade — the page will just re-fetch on next visit.
  }
};
